import React, { useState, useEffect, useMemo } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import 'react-toastify/dist/ReactToastify.css';
import {
  FiAlertTriangle,
  FiArchive,
  FiArrowLeft,
  FiBookOpen,
  FiBriefcase,
  FiCheck,
  FiChevronDown,
  FiClipboard,
  FiClock,
  FiEdit2,
  FiEye,
  FiEyeOff,
  FiHeart,
  FiKey,
  FiLock,
  FiMail,
  FiPlus,
  FiRefreshCw,
  FiCopy,
  FiSearch,
  FiShield,
  FiTrash2,
  FiUser,
  FiUserCheck,
  FiUserPlus,
  FiUsers,
  FiUploadCloud,
  FiLayers,
  FiX,
} from 'react-icons/fi';
import Alerts from './Alerts';
import DropboxImport from './documents/DropboxImport';
import UnitsUpgrade from './admin/UnitsUpgrade';
import Logo from './ui/Logo';
import { API_BASE_URL } from '../lib/session';

const roles = ['Admin', 'Médecin', 'Secrétaire', 'Infirmier(e)', 'Archiviste', 'Gestionnaire', 'Etudiant(e)'];
const accessLevels = ['Affichage', 'Affichage-Modification', 'Affichage-Modification-Suppression', 'Administrateur'];
const services = ["Cuomo", "Ctcv", "Cardiologie", "Réanimation","Cuomo-Cardiologie","Cuomo-Réanimation"];

const CARD =
  'rounded-3xl bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_rgba(0,0,0,0.05)]';

const NAV_ITEMS = [
  { id: 'list', label: 'Utilisateurs', icon: FiUsers },
  { id: 'create', label: 'Nouveau compte', icon: FiUserPlus },
  { id: 'activity', label: "Journal d'activité", icon: FiClock },
  { id: 'dropbox', label: 'Reprise Dropbox', icon: FiUploadCloud },
  { id: 'units', label: 'Lecture par unité', icon: FiLayers },
];

const ROLE_ICONS = {
  Admin: FiShield,
  'Médecin': FiUserCheck,
  'Secrétaire': FiClipboard,
  'Infirmier(e)': FiHeart,
  Archiviste: FiArchive,
  Gestionnaire: FiBriefcase,
  'Etudiant(e)': FiBookOpen,
};

// Ordinal brand-blue ramp, validated light → dark: darker dot = broader privileges.
const ACCESS_LEVEL_META = {
  Affichage: { dot: 'bg-brand-400', description: 'Consultation uniquement' },
  'Affichage-Modification': { dot: 'bg-brand-500', description: 'Consultation et modification' },
  'Affichage-Modification-Suppression': {
    dot: 'bg-brand-700',
    description: 'Consultation, modification et suppression',
  },
  Administrateur: { dot: 'bg-brand-900', description: 'Accès complet à la plateforme' },
};

const plural = (count, singular, pluralForm) => (count > 1 ? pluralForm : singular);

const formatShortDate = (value) =>
  new Date(value).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });

const getInitials = (name = '') =>
  name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase() || '?';

const Avatar = ({ name, large = false }) => (
  <span
    aria-hidden="true"
    className={`flex flex-none items-center justify-center rounded-full bg-mist font-semibold text-ink ring-1 ring-black/[0.06] ${
      large ? 'h-14 w-14 text-[18px]' : 'h-10 w-10 text-[13px]'
    }`}
  >
    {getInitials(name)}
  </span>
);

const UserAvatar = ({ user }) => {
  const RoleIcon = ROLE_ICONS[user.role] || FiUser;
  return (
    <span className="relative flex-none">
      <Avatar name={user.name} />
      <span
        aria-hidden="true"
        className="absolute -bottom-0.5 -right-0.5 flex h-[18px] w-[18px] items-center justify-center rounded-full bg-ink text-white ring-2 ring-white"
      >
        <RoleIcon size={10} />
      </span>
    </span>
  );
};

const RoleLabel = ({ role }) => {
  const RoleIcon = ROLE_ICONS[role] || FiUser;
  return (
    <span className="inline-flex items-center gap-2 text-[14px] text-ink">
      <RoleIcon size={15} aria-hidden="true" className="flex-none text-black/45" />
      {role}
    </span>
  );
};

const AccessPill = ({ level }) => {
  const meta = ACCESS_LEVEL_META[level];
  return (
    <span
      title={meta ? meta.description : undefined}
      className="inline-flex max-w-full items-center gap-2 rounded-full bg-black/[0.04] px-2.5 py-1 text-[12px] font-medium leading-snug text-ink"
    >
      <span
        aria-hidden="true"
        className={`h-2 w-2 flex-none rounded-full ${meta ? meta.dot : 'bg-black/25'}`}
      />
      <span className="min-w-0 break-words">{level}</span>
    </span>
  );
};

const IconButton = ({ label, icon: Icon, onClick, danger = false }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    title={label}
    className={`flex h-9 w-9 items-center justify-center rounded-full border-0 bg-[transparent] text-black/45 transition focus-visible:outline-none focus-visible:ring-2 ${
      danger
        ? 'hover:bg-red-50 hover:text-red-600 focus-visible:ring-red-500/40'
        : 'hover:bg-black/[0.05] hover:text-ink focus-visible:ring-brand-600/40'
    }`}
  >
    <Icon size={16} aria-hidden="true" />
  </button>
);

const SectionHeader = ({ title, description, children }) => (
  <header className="mb-7 flex flex-col gap-[20px] md:flex-row md:items-end md:justify-between">
    <div className="min-w-0">
      <p className="mb-2 text-[12px] font-semibold uppercase tracking-[0.08em] text-brand-600">
        Administration
      </p>
      <h1 className="m-0 text-[28px] font-semibold leading-tight tracking-tight text-ink sm:text-[34px]">
        {title}
      </h1>
      {description && (
        <p className="mb-0 mt-2 max-w-2xl text-[15px] leading-relaxed text-black/50">{description}</p>
      )}
    </div>
    {children && (
      <div className="flex flex-none flex-col gap-[12px] sm:flex-row sm:items-center">{children}</div>
    )}
  </header>
);

const StatTile = ({ label, value, detail, icon: Icon, dark = false, className = '', children }) => (
  <div
    className={`relative overflow-hidden rounded-3xl p-[20px] sm:p-6 ${
      dark ? 'bg-ink text-white' : `${CARD} text-ink`
    } ${className}`}
  >
    {dark && (
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -right-12 -top-16 h-44 w-44 rounded-full bg-brand-600/35 blur-3xl"
      />
    )}
    <div className="relative">
      <span
        className={`absolute right-0 top-0 flex h-9 w-9 items-center justify-center rounded-full ${
          dark ? 'bg-white/10 text-white' : 'bg-mist text-black/55'
        }`}
      >
        <Icon size={16} aria-hidden="true" />
      </span>
      <p className={`m-0 pr-11 text-[13px] font-medium ${dark ? 'text-white/60' : 'text-black/50'}`}>
        {label}
      </p>
      <p
        className={`mb-0 mt-[20px] text-[34px] font-semibold leading-none tracking-tight sm:text-[40px] ${
          dark ? 'text-white' : 'text-ink'
        }`}
      >
        {value}
      </p>
      <p className={`mb-0 mt-[12px] text-[13px] ${dark ? 'text-white/50' : 'text-black/45'}`}>{detail}</p>
      {children}
    </div>
  </div>
);

const INPUT_BASE =
  'h-12 w-full rounded-xl border-[1px] border-solid border-black/10 bg-white text-[15px] text-ink outline-none transition placeholder:text-black/30 focus:border-brand-600 focus:ring-4 focus:ring-brand-600/10';

const FIELD_LABEL = 'mb-1.5 block text-[13px] font-medium text-black/60';

const TextField = ({ id, label, icon: Icon, trailing, ...inputProps }) => (
  <div>
    <label htmlFor={id} className={FIELD_LABEL}>
      {label}
    </label>
    <div className="relative">
      <Icon
        size={17}
        aria-hidden="true"
        className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-black/35"
      />
      <input id={id} {...inputProps} className={`${INPUT_BASE} pl-11 ${trailing ? 'pr-12' : 'pr-4'}`} />
      {trailing}
    </div>
  </div>
);

const SelectField = ({ id, label, placeholder, options, ...selectProps }) => (
  <div>
    <label htmlFor={id} className={FIELD_LABEL}>
      {label}
    </label>
    <div className="relative">
      <select
        id={id}
        {...selectProps}
        className={`${INPUT_BASE} cursor-pointer appearance-none pl-4 pr-11 invalid:text-black/35`}
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option} value={option} className="text-ink">
            {option}
          </option>
        ))}
      </select>
      <FiChevronDown
        size={17}
        aria-hidden="true"
        className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-black/40"
      />
    </div>
  </div>
);

const FormSection = ({ title, description, children }) => (
  <div className="grid grid-cols-1 gap-[20px] lg:grid-cols-[200px_minmax(0,1fr)] lg:gap-8">
    <div>
      <h2 className="m-0 text-[15px] font-semibold text-ink">{title}</h2>
      <p className="mb-0 mt-1 text-[13px] leading-relaxed text-black/45">{description}</p>
    </div>
    <div className="min-w-0">{children}</div>
  </div>
);

const Divider = () => <div aria-hidden="true" className="my-8 h-px bg-black/[0.06]" />;

const PRIMARY_BUTTON =
  'flex items-center justify-center gap-2 rounded-full border-0 bg-brand-600 font-medium text-white transition hover:bg-brand-700 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand-600/25';

const SECONDARY_BUTTON =
  'flex items-center justify-center gap-2 rounded-full border-0 bg-black/[0.05] font-medium text-ink transition hover:bg-black/[0.09] active:scale-[0.98] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-black/10';

const BUTTON_LG = 'h-12 px-6 text-[15px]';
const BUTTON_MD = 'h-11 px-[20px] text-[14px]';
const BUTTON_SM = 'h-10 px-[20px] text-[14px]';

const PAGE_SIZE = 15;

const USERS_URL = `${API_BASE_URL}/users`;
const MIN_PASSWORD_LENGTH = 8;
const MIN_ACCESS_CODE_LENGTH = 6;
const EMPTY_FORM = {
  name: '',
  password: '',
  role: '',
  accessLevel: '',
  service: '',
  email: '',
  areaAccess: [],
  accessCode: '',
};

// Same defaults as the server (server/utils/permissions.js), pre-ticked in the form.
const defaultAreas = ({ role, accessLevel }) => {
  const areas = [];
  if (
    ['Admin', 'Gestionnaire'].includes(role) ||
    (['Médecin', 'Secrétaire'].includes(role) && accessLevel === 'Administrateur')
  ) {
    areas.push('payments');
  }
  if (['Admin', 'Archiviste', 'Médecin'].includes(role)) areas.push('archives');
  return areas;
};

const AREA_OPTIONS = [
  { id: 'payments', label: 'Paiements', description: 'Solvabilité et paiements des patients' },
  { id: 'archives', label: 'Archives', description: 'Archives et liste des archives' },
];
const AREA_LABELS = Object.fromEntries(AREA_OPTIONS.map(({ id, label }) => [id, label]));

const TABLE_HEAD =
  'whitespace-nowrap bg-mist/70 px-[16px] py-[12px] text-[12px] font-medium text-black/45 first:pl-6 last:pr-6';

const TABLE_CELL = 'px-[16px] py-3.5 align-middle first:pl-6 last:pr-6';

const RoleDistribution = ({ rows, total, isLoading }) => (
  <aside aria-labelledby="role-distribution-title" className={`${CARD} p-6 xl:sticky xl:top-10`}>
    <h2 id="role-distribution-title" className="m-0 text-[17px] font-semibold tracking-tight text-ink">
      Répartition par rôle
    </h2>
    <p className="mb-0 mt-1 text-[13px] text-black/45">
      {isLoading
        ? 'Chargement…'
        : `Part de chaque rôle sur ${total} ${plural(total, 'compte', 'comptes')}`}
    </p>

    {isLoading ? (
      <ul aria-hidden="true" className="mb-0 mt-6 list-none space-y-5 p-0">
        {[0, 1, 2, 3, 4].map((i) => (
          <li key={i} className="space-y-2.5">
            <span className="block h-3 w-2/3 animate-pulse rounded-full bg-black/[0.05]" />
            <span className="block h-1.5 w-full animate-pulse rounded-full bg-black/[0.05]" />
          </li>
        ))}
      </ul>
    ) : (
      <ul className="mb-0 mt-6 list-none space-y-4 p-0">
        {rows.map(({ role, count }) => {
          const share = total ? Math.round((count / total) * 100) : 0;
          const RoleIcon = ROLE_ICONS[role] || FiUser;
          return (
            <li
              key={role}
              title={`${role} : ${count} ${plural(count, 'compte', 'comptes')} (${share} %)`}
              className="group"
            >
              <div className="flex items-center justify-between gap-[12px]">
                <span className="flex min-w-0 items-center gap-2.5">
                  <span className="flex h-8 w-8 flex-none items-center justify-center rounded-xl bg-mist text-black/55">
                    <RoleIcon size={15} aria-hidden="true" />
                  </span>
                  <span className="truncate text-[14px] text-ink">{role}</span>
                </span>
                <span className="flex-none text-[13px] tabular-nums text-black/45">
                  <span className="font-semibold text-ink">{count}</span> · {share}
                  {' '}%
                </span>
              </div>
              <div aria-hidden="true" className="ml-[42px] mt-2 h-1.5 rounded-r-[4px] bg-brand-50">
                <div
                  className="h-full rounded-r-[4px] bg-brand-600 transition-[width] duration-500 group-hover:bg-brand-700"
                  style={{ width: `${share}%` }}
                />
              </div>
            </li>
          );
        })}
      </ul>
    )}
  </aside>
);

const UserManagement = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [userData, setUserData] = useState(EMPTY_FORM);
  const [editUserId, setEditUserId] = useState(null);
  const [areasTouched, setAreasTouched] = useState(false);
  const [showFormPassword, setShowFormPassword] = useState(false);
  const [showFormAccessCode, setShowFormAccessCode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [activeSection, setActiveSection] = useState('list'); // Default view is 'list'
  const [expandedUsers, setExpandedUsers] = useState({});

  const toggleUserExpand = (userId) => {
    setExpandedUsers(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axios.get(USERS_URL);
        setUsers(response.data);
      } catch (error) {
        setLoadError(true);
        toast.error('Erreur de chargement des utilisateurs');
      } finally {
        setIsLoadingUsers(false);
      }
    };
    fetchUsers();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSaving) return;

    const creating = editUserId === null;
    const { name, password, role, accessLevel, service, email, areaAccess, accessCode } = userData;
    if (!name || !role || !accessLevel || !service || (creating && !password)) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    if (password && password.length < MIN_PASSWORD_LENGTH) {
      toast.error(`Le mot de passe doit contenir au moins ${MIN_PASSWORD_LENGTH} caractères`);
      return;
    }
    if (accessCode && accessCode.trim().length < MIN_ACCESS_CODE_LENGTH) {
      toast.error(`Le code d'accès doit contenir au moins ${MIN_ACCESS_CODE_LENGTH} caractères`);
      return;
    }

    const payload = { name, role, accessLevel, service, email: email || '', areaAccess };
    if (password) payload.password = password;
    if (accessCode) payload.accessCode = accessCode.trim();

    setIsSaving(true);
    try {
      if (!creating) {
        const response = await axios.put(`${USERS_URL}/${users[editUserId]._id}`, payload);
        setUsers(prevUsers => {
          const newUsers = [...prevUsers];
          newUsers[editUserId] = response.data;
          return newUsers;
        });
        toast.success('Compte modifié');
      } else {
        const response = await axios.post(USERS_URL, payload);
        setUsers(prevUsers => [...prevUsers, response.data]);
        toast.success('Compte créé');
      }

      setEditUserId(null);
      setUserData(EMPTY_FORM);
      setAreasTouched(false);
      setShowFormPassword(false);
      setShowFormAccessCode(false);
    } catch (error) {
      toast.error(
        (error.response && error.response.data && error.response.data.message) ||
          (creating ? 'Erreur lors de la création du compte' : 'Erreur lors de la modification du compte')
      );
    } finally {
      setIsSaving(false);
    }
  };
  const [searchTerm, setSearchTerm] = useState('');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [searchTerm]);

  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.accessLevel.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.service.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (user.email || '').toLowerCase().includes(searchTerm.toLowerCase())
  );
  const visibleUsers = filteredUsers.slice(0, visibleCount);
  const hiddenCount = filteredUsers.length - visibleUsers.length;
  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserData((prevData) => {
      const next = { ...prevData, [name]: value };
      // Sensitive spaces follow the role until the administrator ticks them by hand
      // (on an existing account, only a change of role resets them).
      const followsRole = name === 'role' || (name === 'accessLevel' && editUserId === null);
      if (followsRole && !areasTouched) next.areaAccess = defaultAreas(next);
      return next;
    });
  };

  const handleResetPassword = async (user) => {
    if (!window.confirm(`Réinitialiser le mot de passe de ${user.name} ? Son mot de passe actuel cessera de fonctionner.`)) return;
    try {
      const response = await axios.post(`${USERS_URL}/${user._id}/reset-password`);
      setUsers((prev) => prev.map((u) => (u._id === user._id ? { ...u, mustChangePassword: true } : u)));
      setCopied(false);
      setResetResult(response.data);
    } catch (error) {
      toast.error(
        (error.response && error.response.data && error.response.data.message) ||
          'Le mot de passe n’a pas pu être réinitialisé'
      );
    }
  };

  const copyTemporaryPassword = async () => {
    try {
      await navigator.clipboard.writeText(resetResult.temporaryPassword);
      setCopied(true);
    } catch (error) {
      toast.info('Copie impossible : notez le mot de passe affiché.');
    }
  };

  useEffect(() => {
    if (!resetResult) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setResetResult(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [resetResult]);

  const handleDelete = async (id) => {
    if (window.confirm("Confirmer la suppression ?")) {
      try {
        await axios.delete(`${USERS_URL}/${id}`);
        setUsers(users.filter(user => user._id !== id));
        toast.success('Compte supprimé');
      } catch (error) {
        toast.error(
          (error.response && error.response.data && error.response.data.message) ||
            'Erreur lors de la suppression'
        );
      }
    }
  };

  const handleEdit = (index) => {
    const { name, role, accessLevel, service, email, areaAccess } = users[index];
    setUserData({
      name,
      role,
      accessLevel,
      service,
      email: email || '',
      password: '',
      areaAccess: areaAccess || [],
      accessCode: '',
    });
    setEditUserId(index);
    setAreasTouched(false);
    setShowFormPassword(false);
    setShowFormAccessCode(false);
    setActiveSection('create');
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const cancelEdit = () => {
    setEditUserId(null);
    setUserData(EMPTY_FORM);
    setAreasTouched(false);
    setShowFormPassword(false);
    setShowFormAccessCode(false);
  };

  const toggleArea = (area) => {
    setAreasTouched(true);
    setUserData((prev) => ({
      ...prev,
      areaAccess: prev.areaAccess.includes(area)
        ? prev.areaAccess.filter((item) => item !== area)
        : [...prev.areaAccess, area],
    }));
  };

  const stats = useMemo(() => {
    const roleCounts = {};
    users.forEach((user) => {
      const key = user.role || 'Non défini';
      roleCounts[key] = (roleCounts[key] || 0) + 1;
    });
    const roleRows = [
      ...roles.map((role) => ({ role, count: roleCounts[role] || 0 })),
      ...Object.keys(roleCounts)
        .filter((role) => !roles.includes(role))
        .map((role) => ({ role, count: roleCounts[role] })),
    ].sort((a, b) => b.count - a.count);

    return {
      roleRows,
      rolesInUse: Object.keys(roleCounts).length,
      emailCount: users.filter((user) => user.email).length,
      adminAccessCount: users.filter((user) => user.accessLevel === 'Administrateur').length,
    };
  }, [users]);

  const isEditing = editUserId !== null;
  const editingName = isEditing && users[editUserId] ? users[editUserId].name : '';
  const editingHasCode = Boolean(isEditing && users[editUserId] && users[editUserId].hasAccessCode);
  const editingCodeSetAt = editingHasCode ? users[editUserId].accessCodeSetAt : null;
  const trackedFields = [userData.name, userData.email, userData.role, userData.accessLevel, userData.service];
  if (!isEditing) trackedFields.push(userData.password);
  const filledFields = trackedFields.filter(Boolean).length;
  const emailShare = users.length ? Math.round((stats.emailCount / users.length) * 100) : 0;

  const renderEmailOrigin = (user) => {
    if (user.emailVerifiedAt) {
      return (
        <>
          <FiCheck size={11} aria-hidden="true" className="flex-none text-emerald-600" />
          {user.emailSource === 'self' ? 'Enregistrée par l’utilisateur' : 'Confirmée'} le{' '}
          {formatShortDate(user.emailVerifiedAt)}
        </>
      );
    }
    return user.emailSource === 'admin' ? 'Saisie par l’administrateur · non confirmée' : null;
  };

  const renderTemporaryPassword = (user) =>
    user.mustChangePassword ? (
      <span
        title="Mot de passe défini par l'administrateur : la personne devra choisir le sien à sa prochaine connexion."
        className="mt-1 inline-flex items-center gap-1 rounded-full bg-black/[0.05] px-2 py-0.5 text-[11.5px] font-medium text-black/60"
      >
        <FiRefreshCw size={10} aria-hidden="true" />
        Mot de passe temporaire
      </span>
    ) : null;

  const renderAreas = (user) => {
    const areas = user.areaAccess || [];
    if (!areas.length) return null;
    return (
      <span className="mt-1.5 flex flex-wrap items-center gap-1.5">
        {areas.map((area) => (
          <span
            key={area}
            className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2 py-0.5 text-[11.5px] font-medium text-brand-800"
          >
            <FiLock size={10} aria-hidden="true" />
            {AREA_LABELS[area] || area}
          </span>
        ))}
        {!user.hasAccessCode && (
          <span
            title="Espace sensible autorisé, mais aucun code d'accès n'est défini : la personne ne peut pas encore l'ouvrir."
            className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11.5px] font-medium text-amber-800"
          >
            <FiAlertTriangle size={10} aria-hidden="true" />
            Code à définir
          </span>
        )}
      </span>
    );
  };

  const renderEmail = (user) =>
    user.email ? (
      <span className="block min-w-0">
        <span title={user.email} className="block max-w-[240px] truncate text-[13px] text-ink">
          {user.email}
        </span>
        {renderEmailOrigin(user) && (
          <span className="mt-0.5 flex items-center gap-1 text-[11.5px] text-black/40">{renderEmailOrigin(user)}</span>
        )}
      </span>
    ) : (
      <span
        title="L’adresse sera demandée à la prochaine connexion, puis confirmée par un code."
        className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-[12px] font-medium text-amber-800"
      >
        <FiAlertTriangle size={12} aria-hidden="true" className="text-amber-600" />
        Non enregistré
      </span>
    );

  const renderUsersBody = () => {
    if (isLoadingUsers) {
      return (
        <ul aria-hidden="true" className="m-0 list-none p-0">
          {[0, 1, 2, 3, 4].map((i) => (
            <li
              key={i}
              className="flex items-center gap-[12px] border-x-0 border-b-0 border-t border-solid border-black/[0.05] px-6 py-[16px]"
            >
              <span className="h-10 w-10 flex-none animate-pulse rounded-full bg-black/[0.05]" />
              <span className="flex-1 space-y-2">
                <span className="block h-3 w-40 max-w-full animate-pulse rounded-full bg-black/[0.05]" />
                <span className="block h-2.5 w-24 animate-pulse rounded-full bg-black/[0.04]" />
              </span>
            </li>
          ))}
        </ul>
      );
    }

    if (filteredUsers.length === 0) {
      if (searchTerm) {
        return (
          <div className="flex flex-col items-center border-x-0 border-b-0 border-t border-solid border-black/[0.05] px-6 py-14 text-center">
            <p className="m-0 text-[15px] font-medium text-ink">
              Aucun compte ne correspond à « {searchTerm} »
            </p>
            <p className="mb-0 mt-1 text-[14px] text-black/45">
              La recherche porte sur le nom, le rôle, le niveau d'accès et le service.
            </p>
            <button type="button" onClick={() => setSearchTerm('')} className={`${SECONDARY_BUTTON} ${BUTTON_SM} mt-[20px]`}>
              <FiX size={15} aria-hidden="true" />
              Effacer la recherche
            </button>
          </div>
        );
      }
      return (
        <div className="flex flex-col items-center border-x-0 border-b-0 border-t border-solid border-black/[0.05] px-6 py-14 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-mist text-black/40">
            <FiUsers size={20} aria-hidden="true" />
          </span>
          <p className="mb-0 mt-[16px] text-[15px] font-medium text-ink">
            {loadError ? 'Impossible de charger les comptes' : 'Aucun compte pour le moment'}
          </p>
          <p className="mb-0 mt-1 max-w-sm text-[14px] text-black/45">
            {loadError
              ? 'Vérifiez la connexion au serveur, puis rechargez la page.'
              : 'Créez le premier compte pour donner accès à un membre du personnel.'}
          </p>
          {!loadError && (
            <button type="button" onClick={() => setActiveSection('create')} className={`${PRIMARY_BUTTON} ${BUTTON_MD} mt-[20px]`}>
              <FiPlus size={16} aria-hidden="true" />
              Créer un compte
            </button>
          )}
        </div>
      );
    }

    return (
      <>
        <div className="hidden overflow-x-auto lg:block">
          <table className="w-full min-w-[760px] border-collapse text-left">
            <thead>
              <tr>
                <th scope="col" className={TABLE_HEAD}>Utilisateur</th>
                <th scope="col" className={TABLE_HEAD}>Niveau d'accès</th>
                <th scope="col" className={TABLE_HEAD}>Email</th>
                <th scope="col" className={`${TABLE_HEAD} text-right`}>
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {visibleUsers.map((user) => (
                <tr
                  key={user._id}
                  className="border-x-0 border-b-0 border-t border-solid border-black/[0.05] transition-colors hover:bg-mist/60"
                >
                  <td className={TABLE_CELL}>
                    <div className="flex min-w-0 items-center gap-[12px]">
                      <UserAvatar user={user} />
                      <div className="min-w-0">
                        <p className="m-0 truncate text-[14px] font-medium text-ink">{user.name}</p>
                        <p className="m-0 truncate text-[13px] text-black/45">
                          {user.role} · {user.service}
                        </p>
                        {renderTemporaryPassword(user)}
                      </div>
                    </div>
                  </td>
                  <td className={`${TABLE_CELL} whitespace-nowrap`}>
                    <AccessPill level={user.accessLevel} />
                    {renderAreas(user)}
                  </td>
                  <td className={`${TABLE_CELL} whitespace-nowrap`}>{renderEmail(user)}</td>
                  <td className={`${TABLE_CELL} whitespace-nowrap text-right`}>
                    <div className="inline-flex items-center gap-1">
                      <IconButton
                        label={`Modifier ${user.name}`}
                        icon={FiEdit2}
                        onClick={() => handleEdit(users.indexOf(user))}
                      />
                      <IconButton
                        label={`Réinitialiser le mot de passe de ${user.name}`}
                        icon={FiRefreshCw}
                        onClick={() => handleResetPassword(user)}
                      />
                      <IconButton
                        label={`Supprimer ${user.name}`}
                        icon={FiTrash2}
                        danger
                        onClick={() => handleDelete(user._id)}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <ul className="m-0 list-none p-0 lg:hidden">
          {visibleUsers.map((user) => {
            const expanded = !!expandedUsers[user._id];
            const panelId = `user-details-${user._id}`;
            return (
              <li key={user._id} className="border-x-0 border-b-0 border-t border-solid border-black/[0.05]">
                <button
                  type="button"
                  onClick={() => toggleUserExpand(user._id)}
                  aria-expanded={expanded}
                  aria-controls={panelId}
                  className="flex w-full items-center gap-[12px] border-0 bg-[transparent] px-[20px] py-3.5 text-left transition hover:bg-mist/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-600/40"
                >
                  <UserAvatar user={user} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[15px] font-medium text-ink">{user.name}</span>
                    <span className="block truncate text-[13px] text-black/45">
                      {user.role} · {user.service}
                    </span>
                  </span>
                  <FiChevronDown
                    size={18}
                    aria-hidden="true"
                    className={`flex-none text-black/35 transition-transform ${expanded ? 'rotate-180' : ''}`}
                  />
                </button>
                {expanded && (
                  <div id={panelId} className="px-[20px] pb-[20px]">
                    <dl className="m-0 space-y-4 rounded-2xl bg-mist/70 p-[16px]">
                      <div>
                        <dt className="text-[12px] font-medium text-black/45">Niveau d'accès</dt>
                        <dd className="mb-0 mt-1.5">
                          <AccessPill level={user.accessLevel} />
                          {renderAreas(user)}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-[12px] font-medium text-black/45">Email</dt>
                        <dd className="mb-0 mt-1.5">{renderEmail(user)}</dd>
                      </div>
                      {user.mustChangePassword && <div>{renderTemporaryPassword(user)}</div>}
                    </dl>
                    <button
                      type="button"
                      onClick={() => handleResetPassword(user)}
                      className={`${SECONDARY_BUTTON} mt-[12px] h-11 w-full px-[16px] text-[14px]`}
                    >
                      <FiRefreshCw size={15} aria-hidden="true" />
                      Réinitialiser le mot de passe
                    </button>
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => handleEdit(users.indexOf(user))}
                        className={`${SECONDARY_BUTTON} h-11 px-[16px] text-[14px]`}
                      >
                        <FiEdit2 size={15} aria-hidden="true" />
                        Modifier
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(user._id)}
                        className="flex h-11 items-center justify-center gap-2 rounded-full border-0 bg-red-50 px-[16px] text-[14px] font-medium text-red-600 transition hover:bg-red-100 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-red-500/20"
                      >
                        <FiTrash2 size={15} aria-hidden="true" />
                        Supprimer
                      </button>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>

        {hiddenCount > 0 && (
          <div className="flex flex-col items-center gap-[12px] border-x-0 border-b-0 border-t border-solid border-black/[0.05] px-6 py-[20px] sm:flex-row sm:justify-between">
            <p className="mb-0 text-[13px] tabular-nums text-black/45">
              {visibleUsers.length} sur {filteredUsers.length} affichés
            </p>
            <button
              type="button"
              onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
              className={`${SECONDARY_BUTTON} ${BUTTON_SM}`}
            >
              <FiChevronDown size={15} aria-hidden="true" />
              Afficher {Math.min(PAGE_SIZE, hiddenCount)} de plus
            </button>
          </div>
        )}
      </>
    );
  };

  const usersSubtitle = isLoadingUsers
    ? 'Chargement des comptes…'
    : searchTerm
      ? `${filteredUsers.length} ${plural(filteredUsers.length, 'résultat', 'résultats')} sur ${users.length}`
      : `${users.length} ${plural(users.length, 'compte', 'comptes')} au total`;

  return (
    <div className="min-h-screen bg-mist font-sans text-ink lg:flex">
      <aside
        aria-label="Navigation de l'administration"
        className="sticky top-0 z-30 hidden h-screen w-[88px] flex-none flex-col items-center bg-ink py-6 lg:flex"
      >
        <Logo size={44} title="CTCV · Administration" className="rounded-2xl" />
        <span aria-hidden="true" className="mt-6 h-px w-8 bg-white/10" />

        <nav className="mt-6 flex flex-1 flex-col items-center gap-2">
          {NAV_ITEMS.map(({ id, label, icon: Icon }) => {
            const active = activeSection === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => setActiveSection(id)}
                aria-label={label}
                aria-current={active ? 'page' : undefined}
                className={`group relative flex h-12 w-12 items-center justify-center rounded-2xl border-0 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 ${
                  active ? 'bg-white/[0.12] text-white' : 'bg-[transparent] text-white/45 hover:bg-white/[0.06] hover:text-white'
                }`}
              >
                {active && (
                  <span aria-hidden="true" className="absolute -left-[20px] h-6 w-1 rounded-r-full bg-white" />
                )}
                <Icon size={20} aria-hidden="true" />
                <span className="pointer-events-none absolute left-full ml-4 whitespace-nowrap rounded-lg bg-ink px-[12px] py-1.5 text-[12px] font-medium text-white [opacity:0] shadow-[0_8px_24px_rgba(0,0,0,0.25)] ring-1 ring-white/10 transition group-hover:[opacity:1] group-focus-visible:[opacity:1]">
                  {label}
                </span>
              </button>
            );
          })}
        </nav>

        <button
          type="button"
          onClick={() => navigate('/role')}
          aria-label="Retour aux espaces"
          className="group relative flex h-12 w-12 items-center justify-center rounded-2xl border-0 bg-[transparent] text-white/45 transition hover:bg-white/[0.06] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
        >
          <FiArrowLeft size={20} aria-hidden="true" />
          <span className="pointer-events-none absolute left-full ml-4 whitespace-nowrap rounded-lg bg-ink px-[12px] py-1.5 text-[12px] font-medium text-white [opacity:0] shadow-[0_8px_24px_rgba(0,0,0,0.25)] ring-1 ring-white/10 transition group-hover:[opacity:1] group-focus-visible:[opacity:1]">
            Retour aux espaces
          </span>
        </button>
      </aside>

      <div className="min-w-0 flex-1">
        <header className="sticky top-0 z-20 border-x-0 border-t-0 border-b border-solid border-black/[0.05] bg-mist/85 backdrop-blur-xl lg:hidden">
          <div className="flex h-14 items-center justify-between px-[16px] sm:px-6">
            <div className="flex items-center gap-2.5">
              <Logo size={32} />
              <span className="text-[15px] font-semibold tracking-tight text-ink">Administration</span>
            </div>
            <button
              type="button"
              onClick={() => navigate('/role')}
              className="flex h-10 items-center gap-1.5 rounded-full border-0 bg-black/[0.05] px-[16px] text-[14px] font-medium text-ink transition hover:bg-black/[0.09]"
            >
              <FiArrowLeft size={15} aria-hidden="true" />
              Espaces
            </button>
          </div>
          <nav
            aria-label="Sections de l'administration"
            className="flex gap-2 overflow-x-auto px-[16px] pb-[12px] sm:px-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {NAV_ITEMS.map(({ id, label, icon: Icon }) => {
              const active = activeSection === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => setActiveSection(id)}
                  aria-current={active ? 'page' : undefined}
                  className={`flex h-11 flex-none items-center gap-2 rounded-full border-0 py-1 pl-1 pr-4 text-[14px] font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600/40 ${
                    active
                      ? 'bg-brand-600 text-white shadow-[0_6px_16px_rgba(0,113,227,0.25)]'
                      : 'bg-white text-ink ring-1 ring-black/[0.06]'
                  }`}
                >
                  <span
                    className={`flex h-9 w-9 items-center justify-center rounded-full ${
                      active ? 'bg-white/20 text-white' : 'bg-ink text-white'
                    }`}
                  >
                    <Icon size={16} aria-hidden="true" />
                  </span>
                  {label}
                </button>
              );
            })}
          </nav>
        </header>

        <main className="mx-auto w-full max-w-[1320px] px-[16px] pb-16 pt-6 sm:px-6 sm:pt-8 lg:px-10 lg:pt-10">
          {activeSection === 'list' && (
            <>
              <SectionHeader
                title="Comptes du personnel"
                description="Gérez les accès des équipes CTCV et CUOMO : rôles, niveaux d'accès, services et adresses email."
              >
                <div className="relative w-full md:w-72">
                  <FiSearch
                    size={16}
                    aria-hidden="true"
                    className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-black/35"
                  />
                  <input
                    type="text"
                    aria-label="Rechercher un compte"
                    placeholder="Nom, rôle, service, email…"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="h-11 w-full rounded-full border-[1px] border-solid border-black/[0.06] bg-white pl-11 pr-10 text-[14px] text-ink shadow-[0_1px_2px_rgba(0,0,0,0.04)] outline-none transition placeholder:text-black/35 focus:border-brand-600 focus:ring-4 focus:ring-brand-600/10"
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      onClick={() => setSearchTerm('')}
                      aria-label="Effacer la recherche"
                      className="absolute right-1.5 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border-0 bg-[transparent] text-black/40 transition hover:bg-black/[0.05] hover:text-black/70"
                    >
                      <FiX size={15} aria-hidden="true" />
                    </button>
                  )}
                </div>
                <button type="button" onClick={() => setActiveSection('create')} className={`${PRIMARY_BUTTON} ${BUTTON_MD}`}>
                  <FiPlus size={17} aria-hidden="true" />
                  Nouveau compte
                </button>
              </SectionHeader>

              <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_300px] xl:items-start">
                <div className="min-w-0 space-y-6">
                  <div className="grid grid-cols-2 gap-[16px] md:grid-cols-3">
                    <StatTile
                      dark
                      className="col-span-2 md:col-span-1"
                      icon={FiUsers}
                      label="Comptes enregistrés"
                      value={isLoadingUsers ? '—' : users.length}
                      detail={
                        isLoadingUsers
                          ? 'Chargement…'
                          : `Répartis sur ${stats.rolesInUse} ${plural(stats.rolesInUse, 'rôle', 'rôles')}`
                      }
                    />
                    <StatTile
                      icon={FiMail}
                      label="Emails renseignés"
                      value={isLoadingUsers ? '—' : stats.emailCount}
                      detail={isLoadingUsers ? 'Chargement…' : `Sur ${users.length} · complétés à la connexion`}
                    >
                      {!isLoadingUsers && (
                        <div
                          role="meter"
                          aria-label="Comptes avec une adresse email"
                          aria-valuemin={0}
                          aria-valuemax={users.length}
                          aria-valuenow={stats.emailCount}
                          className="mt-[12px] h-1.5 rounded-r-[4px] bg-brand-50"
                        >
                          <div
                            className="h-full rounded-r-[4px] bg-brand-600 transition-[width] duration-500"
                            style={{ width: `${emailShare}%` }}
                          />
                        </div>
                      )}
                    </StatTile>
                    <StatTile
                      icon={FiKey}
                      label="Accès complet"
                      value={isLoadingUsers ? '—' : stats.adminAccessCount}
                      detail="Niveau Administrateur"
                    />
                  </div>

                  <section aria-labelledby="users-title" className={`${CARD} min-w-0 overflow-hidden`}>
                    <div className="px-6 pb-[16px] pt-[20px] sm:pt-6">
                      <h2 id="users-title" className="m-0 text-[17px] font-semibold tracking-tight text-ink">
                        Liste des utilisateurs
                      </h2>
                      <p className="mb-0 mt-0.5 text-[13px] text-black/45" aria-live="polite">
                        {usersSubtitle}
                      </p>
                    </div>
                    {renderUsersBody()}
                  </section>
                </div>

                <RoleDistribution rows={stats.roleRows} total={users.length} isLoading={isLoadingUsers} />
              </div>
            </>
          )}

          {activeSection === 'create' && (
            <>
              <SectionHeader
                title={isEditing ? 'Modifier le compte' : 'Nouveau compte'}
                description={
                  isEditing
                    ? `Vous modifiez le compte de ${editingName}.`
                    : "Créez l'accès d'un membre du personnel. Son rôle et son niveau d'accès déterminent ce qu'il pourra consulter et modifier."
                }
              />

              <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_320px] xl:items-start">
                <form onSubmit={handleSubmit} className={`${CARD} min-w-0 p-6 sm:p-8`}>
                  <FormSection
                    title="Identifiants"
                    description="Ce que la personne saisira pour se connecter. Sans email, elle enregistrera la sienne à sa prochaine connexion."
                  >
                    <div className="grid grid-cols-1 gap-[20px] sm:grid-cols-2">
                      <TextField
                        id="name"
                        name="name"
                        label="Nom d'utilisateur"
                        icon={FiUser}
                        placeholder="ex : adiop"
                        value={userData.name}
                        onChange={handleChange}
                        autoComplete="off"
                        required
                      />
                      <TextField
                        id="email"
                        name="email"
                        type="email"
                        label="Email professionnel"
                        icon={FiMail}
                        placeholder="Facultatif : demandé à la connexion"
                        value={userData.email}
                        onChange={handleChange}
                        autoComplete="off"
                      />
                      <div className="sm:col-span-2">
                        <TextField
                          id="password"
                          name="password"
                          type={showFormPassword ? 'text' : 'password'}
                          label={isEditing ? 'Nouveau mot de passe' : 'Mot de passe'}
                          icon={FiLock}
                          placeholder={
                            isEditing
                              ? 'Laisser vide pour conserver le mot de passe actuel'
                              : `${MIN_PASSWORD_LENGTH} caractères minimum`
                          }
                          value={userData.password}
                          onChange={handleChange}
                          autoComplete="new-password"
                          minLength={MIN_PASSWORD_LENGTH}
                          required={!isEditing}
                          trailing={
                            <button
                              type="button"
                              onClick={() => setShowFormPassword((v) => !v)}
                              aria-label={showFormPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                              className="absolute right-1 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-lg border-0 bg-[transparent] text-black/40 transition hover:text-black/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600/40"
                            >
                              {showFormPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                            </button>
                          }
                        />
                        <p className="mb-0 mt-2 text-[12.5px] text-black/45">
                          {isEditing
                            ? "Les mots de passe sont chiffrés : personne ne peut les lire, pas même l'administrateur. Pour débloquer quelqu'un, saisissez-en un nouveau ou utilisez le bouton « Réinitialiser » de la liste : la personne choisira le sien à sa prochaine connexion."
                            : "Mot de passe temporaire : transmettez-le à la personne de vive voix. Elle choisira le sien à sa première connexion."}
                        </p>
                      </div>
                    </div>
                  </FormSection>

                  <Divider />

                  <FormSection
                    title="Affectation"
                    description="Le rôle et le service rattachent le compte à une équipe."
                  >
                    <div className="grid grid-cols-1 gap-[20px] sm:grid-cols-2">
                      <SelectField
                        id="role"
                        name="role"
                        label="Rôle"
                        placeholder="Sélectionner un rôle"
                        options={roles}
                        value={userData.role}
                        onChange={handleChange}
                        required
                      />
                      <SelectField
                        id="service"
                        name="service"
                        label="Service"
                        placeholder="Sélectionner un service"
                        options={services}
                        value={userData.service}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </FormSection>

                  <Divider />

                  <FormSection
                    title="Niveau d'accès"
                    description="Du plus restreint au plus complet."
                  >
                    <div role="radiogroup" aria-label="Niveau d'accès" className="grid grid-cols-1 gap-[12px] sm:grid-cols-2">
                      {accessLevels.map((level, i) => {
                        const meta = ACCESS_LEVEL_META[level];
                        const checked = userData.accessLevel === level;
                        return (
                          <label
                            key={level}
                            htmlFor={`accessLevel-${i}`}
                            className={`flex cursor-pointer items-start gap-[12px] rounded-2xl border-[1px] border-solid p-[16px] transition has-[:focus-visible]:ring-4 has-[:focus-visible]:ring-brand-600/25 ${
                              checked
                                ? 'border-brand-600 bg-brand-50/60'
                                : 'border-black/10 bg-white hover:border-black/20'
                            }`}
                          >
                            <input
                              type="radio"
                              id={`accessLevel-${i}`}
                              name="accessLevel"
                              value={level}
                              checked={checked}
                              onChange={handleChange}
                              required
                              className="sr-only"
                            />
                            <span aria-hidden="true" className={`mt-1.5 h-2.5 w-2.5 flex-none rounded-full ${meta.dot}`} />
                            <span className="min-w-0 flex-1">
                              <span className="block break-words text-[14px] font-medium text-ink">{level}</span>
                              <span className="mt-0.5 block text-[13px] text-black/50">{meta.description}</span>
                            </span>
                            <span
                              aria-hidden="true"
                              className={`flex h-5 w-5 flex-none items-center justify-center rounded-full transition ${
                                checked ? 'bg-brand-600 text-white' : 'bg-[transparent] ring-1 ring-black/15'
                              }`}
                            >
                              {checked && <FiCheck size={12} />}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </FormSection>

                  <Divider />

                  <FormSection
                    title="Espaces sensibles"
                    description="Espaces qui demandent, en plus de la connexion, un code d'accès personnel que vous définissez ici."
                  >
                    <div role="group" aria-label="Espaces sensibles autorisés" className="grid grid-cols-1 gap-[12px] sm:grid-cols-2">
                      {AREA_OPTIONS.map(({ id, label, description }) => {
                        const checked = userData.areaAccess.includes(id);
                        return (
                          <label
                            key={id}
                            htmlFor={`area-${id}`}
                            className={`flex cursor-pointer items-start gap-[12px] rounded-2xl border-[1px] border-solid p-[16px] transition has-[:focus-visible]:ring-4 has-[:focus-visible]:ring-brand-600/25 ${
                              checked ? 'border-brand-600 bg-brand-50/60' : 'border-black/10 bg-white hover:border-black/20'
                            }`}
                          >
                            <input
                              type="checkbox"
                              id={`area-${id}`}
                              checked={checked}
                              onChange={() => toggleArea(id)}
                              className="sr-only"
                            />
                            <span
                              aria-hidden="true"
                              className={`mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-md transition ${
                                checked ? 'bg-brand-600 text-white' : 'bg-white ring-1 ring-black/20'
                              }`}
                            >
                              {checked && <FiCheck size={13} />}
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="flex items-center gap-1.5 text-[14px] font-medium text-ink">
                                <FiLock size={13} aria-hidden="true" className="text-black/45" />
                                {label}
                              </span>
                              <span className="mt-0.5 block text-[13px] text-black/50">{description}</span>
                            </span>
                          </label>
                        );
                      })}
                    </div>

                    <div className="mt-[20px]">
                      <TextField
                        id="accessCode"
                        name="accessCode"
                        type={showFormAccessCode ? 'text' : 'password'}
                        label={editingHasCode ? "Nouveau code d'accès" : "Code d'accès"}
                        icon={FiKey}
                        placeholder={
                          editingHasCode
                            ? 'Laisser vide pour conserver le code actuel'
                            : `${MIN_ACCESS_CODE_LENGTH} caractères minimum`
                        }
                        value={userData.accessCode}
                        onChange={handleChange}
                        autoComplete="off"
                        minLength={MIN_ACCESS_CODE_LENGTH}
                        trailing={
                          <button
                            type="button"
                            onClick={() => setShowFormAccessCode((v) => !v)}
                            aria-label={showFormAccessCode ? 'Masquer le code' : 'Afficher le code'}
                            className="absolute right-1 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-lg border-0 bg-[transparent] text-black/40 transition hover:text-black/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600/40"
                          >
                            {showFormAccessCode ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                          </button>
                        }
                      />
                      <p className="mb-0 mt-2 text-[12.5px] leading-snug text-black/45">
                        {editingHasCode
                          ? `Code défini le ${formatShortDate(editingCodeSetAt)}. Il est chiffré : personne ne peut le lire, vous pouvez seulement le remplacer.`
                          : "Transmettez-le à la personne de vive voix. Il est chiffré dès l'enregistrement et doit être différent du mot de passe."}
                      </p>
                      {userData.areaAccess.length > 0 && !editingHasCode && !userData.accessCode && (
                        <p
                          role="status"
                          className="mb-0 mt-[12px] flex items-start gap-2 rounded-xl bg-amber-50 px-[12px] py-2.5 text-[13px] leading-snug text-amber-900"
                        >
                          <FiAlertTriangle size={15} aria-hidden="true" className="mt-px flex-none text-amber-600" />
                          Sans code, la personne ne pourra pas ouvrir les espaces cochés.
                        </p>
                      )}
                    </div>
                  </FormSection>

                  <div className="mt-8 flex flex-col-reverse gap-[12px] border-x-0 border-b-0 border-t border-solid border-black/[0.06] pt-6 sm:flex-row sm:items-center sm:justify-end">
                    {isEditing && (
                      <button type="button" onClick={cancelEdit} className={`${SECONDARY_BUTTON} ${BUTTON_LG}`}>
                        Annuler
                      </button>
                    )}
                    <button
                      type="submit"
                      disabled={isSaving}
                      className={`${PRIMARY_BUTTON} ${BUTTON_LG} disabled:cursor-not-allowed disabled:opacity-60`}
                    >
                      {isEditing ? (
                        <FiCheck size={17} aria-hidden="true" />
                      ) : (
                        <FiUserPlus size={17} aria-hidden="true" />
                      )}
                      {isSaving ? 'Enregistrement…' : isEditing ? 'Enregistrer les modifications' : 'Créer le compte'}
                    </button>
                  </div>
                </form>

                <aside aria-label="Aperçu du compte" className={`${CARD} p-6 xl:sticky xl:top-10`}>
                  <p className="m-0 text-[12px] font-semibold uppercase tracking-[0.08em] text-black/40">
                    Aperçu
                  </p>
                  <div className="mt-[20px] flex items-center gap-[16px]">
                    <Avatar name={userData.name} large />
                    <div className="min-w-0">
                      <p className="m-0 truncate text-[17px] font-semibold text-ink">
                        {userData.name || "Nom d'utilisateur"}
                      </p>
                      <p className="mb-0 mt-0.5 truncate text-[13px] text-black/45">
                        {userData.service || 'Service non défini'}
                      </p>
                    </div>
                  </div>

                  <dl className="mb-0 mt-6 space-y-4">
                    <div>
                      <dt className="text-[12px] font-medium text-black/45">Rôle</dt>
                      <dd className="mb-0 mt-1.5">
                        {userData.role ? (
                          <RoleLabel role={userData.role} />
                        ) : (
                          <span className="text-[14px] text-black/30">Non défini</span>
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-[12px] font-medium text-black/45">Niveau d'accès</dt>
                      <dd className="mb-0 mt-1.5">
                        {userData.accessLevel ? (
                          <AccessPill level={userData.accessLevel} />
                        ) : (
                          <span className="text-[14px] text-black/30">Non défini</span>
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-[12px] font-medium text-black/45">Espaces sensibles</dt>
                      <dd className="mb-0 mt-1.5 text-[13.5px] leading-snug">
                        {userData.areaAccess.length ? (
                          renderAreas({
                            areaAccess: userData.areaAccess,
                            hasAccessCode: editingHasCode || Boolean(userData.accessCode),
                          })
                        ) : (
                          <span className="text-black/30">Aucun</span>
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-[12px] font-medium text-black/45">Code CTCV-CUOMO</dt>
                      <dd className="mb-0 mt-1.5 text-[13.5px] leading-snug">
                        {userData.email ? (
                          <span className="text-ink">Envoyé chaque semaine à {userData.email}</span>
                        ) : (
                          <span className="text-black/50">
                            L’adresse sera demandée à la prochaine connexion, puis confirmée par un code.
                          </span>
                        )}
                      </dd>
                    </div>
                  </dl>

                  <div className="mt-6 rounded-2xl bg-mist p-[16px]">
                    <div className="flex items-center justify-between text-[13px]">
                      <span className="text-black/55">Champs renseignés</span>
                      <span className="font-semibold tabular-nums text-ink">
                        {filledFields} / {trackedFields.length}
                      </span>
                    </div>
                    <div aria-hidden="true" className="mt-2.5 h-1.5 rounded-r-[4px] bg-brand-100">
                      <div
                        className="h-full rounded-r-[4px] bg-brand-600 transition-[width] duration-300"
                        style={{ width: `${(filledFields / trackedFields.length) * 100}%` }}
                      />
                    </div>
                  </div>
                </aside>
              </div>
            </>
          )}

          {activeSection === 'activity' && (
            <>
              <SectionHeader
                title="Journal d'activité"
                description="Historique des actions effectuées par les utilisateurs sur la plateforme."
              />
              <Alerts />
            </>
          )}

          {activeSection === 'dropbox' && (
            <>
              <SectionHeader
                title="Reprise des anciens fichiers"
                description="Copie dans le Dropbox du service des fichiers restés dans les Dropbox personnels, pour qu’ils s’ouvrent dans la plateforme."
              />
              <DropboxImport />
            </>
          )}

          {activeSection === 'units' && (
            <>
              <SectionHeader
                title="Lecture par unité"
                description="Les messages et les archives ne sont lisibles que par les unités concernées. Cette mise à niveau prépare les entrées écrites avant ce changement ; elle se fait une fois, juste après le déploiement."
              />
              <UnitsUpgrade />
            </>
          )}

        </main>
      </div>

      {resetResult && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-[16px] backdrop-blur-sm"
          onClick={(e) => {
            if (e.target === e.currentTarget) setResetResult(null);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="reset-dialog-title"
            className="w-full max-w-[440px] rounded-3xl bg-white p-8 shadow-[0_24px_64px_rgba(0,0,0,0.25)]"
          >
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
              <FiRefreshCw size={22} aria-hidden="true" />
            </span>
            <h2 id="reset-dialog-title" className="mb-0 mt-[20px] text-[21px] font-semibold tracking-tight text-ink">
              Mot de passe temporaire
            </h2>
            <p className="mb-0 mt-2 text-[14.5px] leading-relaxed text-black/55">
              Transmettez ce mot de passe à <strong className="font-semibold text-ink">{resetResult.name}</strong>. À sa
              prochaine connexion, la page lui demandera d’en choisir un nouveau.
            </p>
            <p className="mb-0 mt-[20px] select-all rounded-2xl bg-mist px-[16px] py-[16px] text-center font-mono text-[24px] font-semibold tracking-[0.08em] text-ink">
              {resetResult.temporaryPassword}
            </p>
            <p className="mb-0 mt-2 text-center text-[12.5px] text-black/45">
              Il ne sera plus affiché après la fermeture de cette fenêtre.
            </p>
            <div className="mt-6 grid grid-cols-1 gap-2 sm:grid-cols-[auto_1fr]">
              <button
                type="button"
                onClick={copyTemporaryPassword}
                autoFocus
                className={`${SECONDARY_BUTTON} ${BUTTON_MD}`}
              >
                {copied ? <FiCheck size={16} aria-hidden="true" /> : <FiCopy size={16} aria-hidden="true" />}
                {copied ? 'Copié' : 'Copier'}
              </button>
              <button type="button" onClick={() => setResetResult(null)} className={`${PRIMARY_BUTTON} ${BUTTON_MD}`}>
                J’ai noté le mot de passe
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
};

export default UserManagement;
